import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { FilterInterface } from './Inventory';
import { useAppSelector } from '../store';
import Loader from '../components/Loader';
import { CarType } from '../enum';

interface IProps {
  keyName: string;
  toggleDrawer: (name: string, open: boolean) => void;
  handleResetFilters: () => void;
  setCommonFilter: React.Dispatch<React.SetStateAction<FilterInterface>>;
  commonFilter: FilterInterface;
  isFilterApplied: () => boolean;
  applyFilter: () => void;
}

const DrawerWrapper = styled('div')(({ theme }: { theme: any }) => ({
  [theme.breakpoints.up('xs')]: {
    width: 300,
  },
  [theme.breakpoints.up('sm')]: {
    width: 300,
  },
  [theme.breakpoints.up('md')]: {
    width: 400,
  },
  [theme.breakpoints.up('lg')]: {
    width: 400,
  },
}));

const InventoryFilterDrawer: React.FC<IProps> = ({
  toggleDrawer,
  keyName,
  handleResetFilters,
  setCommonFilter,
  commonFilter,
  isFilterApplied,
  applyFilter,
}) => {
  const { isLoading } = useAppSelector(state => state.cars);

  return (
    <DrawerWrapper
      sx={{
        pt: 'env(safe-area-inset-top) !important',
        width: 250,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {isLoading ? (
        <Grid
          container
          sx={{
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Loader />
        </Grid>
      ) : (
        <>
          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              py={2}
              px={3}
              spacing={1}
            >
              <Typography
                className='cursor-pointer'
                sx={{
                  fontSize: '24px',
                  fontWeight: 600,
                  color: 'rgba(0, 0, 0, 1)',
                }}
              >
                {'Filters'}
              </Typography>
              <Button
                variant='outlined'
                sx={{
                  minWidth: 'auto',
                  color: 'rgba(75, 75, 75, 1)',
                  height: 42,
                  width: 42,
                  ':hover': {
                    color: 'unset',
                  },
                }}
                onClick={() => toggleDrawer(keyName, false)}
              >
                <CloseIcon sx={{ fontSize: '1.8em' }} />
              </Button>
            </Stack>
            <Divider
              variant='middle'
              sx={{
                mt: 2.5,
              }}
            />

            <Grid
              container
              sx={{ px: 2.5 }}
            >
              <Typography
                className='cursor-pointer'
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: 'rgba(0, 0, 0, 1)',
                  mt: 1,
                }}
              >
                {'Vehicle Type'}
              </Typography>
            </Grid>

            <Grid
              container
              sx={{ px: 2.5 }}
            >
              <FormControl
                fullWidth
                sx={{ mt: 1 }}
              >
                <Select
                  name='carType'
                  labelId='types-label'
                  id='carType'
                  value={commonFilter?.carType || 'all'}
                  onChange={e => {
                    const value = e.target.value;
                    setCommonFilter(prev => ({
                      ...prev,
                      carType: value === 'all' ? '' : value,
                    }));
                  }}
                >
                  <MenuItem value='all'>{'All'}</MenuItem>
                  <MenuItem value='new'>{CarType.new}</MenuItem>
                  <MenuItem value='used'>{CarType.used}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid
              container
              sx={{ px: 2.5 }}
            >
              <Typography
                className='cursor-pointer'
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: 'rgba(0, 0, 0, 1)',
                  mt: 1,
                }}
              >
                {'Price Order By'}
              </Typography>
            </Grid>
            <Grid
              container
              sx={{ px: 2.5 }}
            >
              <FormControl
                fullWidth
                sx={{ mt: 1 }}
              >
                <Select
                  labelId='status-label'
                  id='status'
                  name='priceOrder'
                  value={commonFilter?.priceOrder || ''}
                  displayEmpty
                  onChange={e =>
                    setCommonFilter(prev => ({
                      ...prev,
                      priceOrder: e.target.value,
                    }))
                  }
                >
                  <MenuItem value=''>{'None'}</MenuItem>
                  <MenuItem value='asc'>{'Low to High'}</MenuItem>
                  <MenuItem value='desc'>{'High to Low'}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Divider
              variant='middle'
              sx={{
                mt: 2.5,
                borderColor: 'rgba(219, 219, 219, 1)',
              }}
            />
          </Box>
          <Box
            className='center-class'
            sx={{
              px: 2.5,
              py: 2.5,
              width: '100%',
              bgcolor: 'white',
              position: 'sticky',
              bottom: 0,
              zIndex: 10,
              boxShadow: '0px -6px 12px 0px #9D9D9D40',
            }}
          >
            <Grid container>
              {isFilterApplied() && (
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant='outlined'
                    sx={{
                      color: '#722410',
                      borderColor: '#722410',
                      bgcolor: '#FFFFFF',
                      mt: { xs: 1, md: 1 },
                      fontWeight: 600,
                      borderRadius: '6px',
                      width: '100%',
                      py: 1,
                    }}
                    onClick={handleResetFilters}
                  >
                    {'Clear Filter'}
                  </Button>
                </Grid>
              )}
              <Grid size={{ xs: 12 }}>
                <Button
                  sx={{
                    bgcolor: '#722410',
                    color: '#FFFFFF',
                    mt: { xs: 1, md: 1 },
                    fontWeight: 600,
                    borderRadius: '12px',
                    width: '100%',
                    py: 1,
                  }}
                  onClick={() => {
                    applyFilter();
                    toggleDrawer(keyName, false);
                  }}
                >
                  {'Apply Filter'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </DrawerWrapper>
  );
};

export default InventoryFilterDrawer;
